import { useRef, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import ToolTipUsingPortal from "../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import { InfoIcon } from "../../../../svg/info-icon";
import { capitalizeString } from "../../../../utils/stringModifications";
import { useOrderAssignmentData } from "./useOrderAssignmentData";

/** Displays a hover-triggered tooltip showing today's order assignment counts by assignee. */
const OrderAssignmentInfo = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="order-assignment-info--container mr-2 flex gap-1">
      <div className="order-assignment-info--text">
        View Order Assignment Information
      </div>
      <div
        className="order-assignment-info--icon"
        ref={parentRef}
        onMouseEnter={() => setShowTooltip(true)}
      >
        <InfoIcon />
        {showTooltip && (
          <ToolTipUsingPortal
            title="Order Assignment Information"
            placement="bottom"
            showTooltip={showTooltip}
            parentRef={parentRef}
            offsetPx={10}
            children={
              <div onMouseLeave={() => setShowTooltip(false)}>
                <OrderAssignmentInfoContent />
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default OrderAssignmentInfo;

/** Renders the tooltip body with a total count and per-assignee breakdown. */
const OrderAssignmentInfoContent = () => {
  const { data, loading, error } = useOrderAssignmentData();

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <SpinningLoader /> Loading...
      </div>
    );
  }
  if (error) {
    return <div>Error</div>;
  }
  const assignmentCounts: Record<string, number> =
    data?.facet_results?.["assigned_to_name"] ?? {};

  const sortedAssignmentsCountsByName = Object.entries(assignmentCounts).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );

  return (
    <div className="order-assignment-info-content--container flex max-h-[30rem] w-40 flex-col gap-2 overflow-y-auto">
      <div className="order-assignment-count flex justify-between gap-2 font-bold">
        <div className="assignee-name">Total Orders</div>
        <div className="assignee-count">{data?.total_count ?? 0}</div>
      </div>
      {sortedAssignmentsCountsByName.map(([key, value]) => (
        <div
          className="order-assignment-count flex justify-between gap-2"
          key={key}
        >
          <div className="assignee-name">{capitalizeString(key)}</div>
          <div className="assignee-count">{value}</div>
        </div>
      ))}
    </div>
  );
};
